import * as THREE from 'three';
import { ITEMS, SHOPS, type ItemId, type Wallet } from '../game/Economy';
import { QUESTS, type QuestLog } from '../game/Quests';
import { model } from '../world/Assets';

const $ = <T extends HTMLElement>(sel: string) => document.querySelector<T>(sel)!;
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/** Renders each item model once into a small transparent PNG for shop cards. */
export class Thumbnails {
  private readonly cache = new Map<string, string>();
  private readonly rt = new THREE.WebGLRenderTarget(160, 160);
  private readonly scene = new THREE.Scene();
  private readonly cam = new THREE.PerspectiveCamera(30, 1, 0.01, 100);
  private readonly canvas = document.createElement('canvas');

  constructor(private readonly renderer: THREE.WebGLRenderer) {
    this.canvas.width = this.canvas.height = 160;
    const sun = new THREE.DirectionalLight(0xffffff, 2.4);
    sun.position.set(2, 3, 4);
    this.scene.add(sun);
  }

  get(id: ItemId): string {
    const hit = this.cache.get(id);
    if (hit) return hit;
    const obj = model(ITEMS[id].model);
    obj.rotation.set(-0.35, 0.7, 0.35);
    this.scene.add(obj);
    const box = new THREE.Box3().setFromObject(obj);
    const size = box.getSize(new THREE.Vector3()).length();
    const c = box.getCenter(new THREE.Vector3());
    this.cam.position.copy(c).add(new THREE.Vector3(0, 0, size * 1.9));
    this.cam.lookAt(c);
    const r = this.renderer;
    const prevTarget = r.getRenderTarget();
    const prevColor = r.getClearColor(new THREE.Color());
    const prevAlpha = r.getClearAlpha();
    r.setRenderTarget(this.rt);
    r.setClearColor(0x000000, 0);
    r.clear();
    r.render(this.scene, this.cam);
    const px = new Uint8Array(160 * 160 * 4);
    r.readRenderTargetPixels(this.rt, 0, 0, 160, 160, px);
    r.setRenderTarget(prevTarget);
    r.setClearColor(prevColor, prevAlpha);
    this.scene.remove(obj);
    const ctx = this.canvas.getContext('2d')!;
    const img = ctx.createImageData(160, 160);
    for (let y = 0; y < 160; y++) {
      for (let x = 0; x < 160; x++) {
        const s = ((159 - y) * 160 + x) * 4, d = (y * 160 + x) * 4;
        // Linear render target → rough sRGB for display.
        for (let k = 0; k < 3; k++) img.data[d + k] = Math.round(255 * Math.pow(px[s + k] / 255, 1 / 2.2));
        img.data[d + 3] = px[s + 3] ? 255 : 0;
      }
    }
    ctx.putImageData(img, 0, 0);
    const url = this.canvas.toDataURL('image/png');
    this.cache.set(id, url);
    return url;
  }
}

type MarkerEl = HTMLDivElement;

export class Hud {
  private readonly coinsEl = $('#coins-value');
  private readonly tracker = $('#tracker');
  private readonly toasts = $('#toasts');
  private readonly markers = $('#markers');
  private readonly shop = $('#shop');
  private readonly bag = $('#bag');
  private readonly log = $('#log');
  private readonly markerPool: MarkerEl[] = [];
  private markerUsed = 0;
  private lastCoins: number;
  private openShopId: string | null = null;
  onBuy: (id: ItemId) => void = () => {};
  onEquip: (id: ItemId) => void = () => {};
  onClosePanel: () => void = () => {};

  constructor(private readonly wallet: Wallet, private readonly quests: QuestLog, private readonly thumbs: Thumbnails) {
    this.lastCoins = wallet.coins;
    wallet.subscribe(() => this.refresh());
    for (const el of [this.shop, this.bag, this.log]) {
      el.addEventListener('click', (e) => {
        const t = e.target as HTMLElement;
        if (t === el || t.closest('[data-close]')) this.closePanels();
        const buy = t.closest<HTMLElement>('[data-buy]');
        if (buy) this.onBuy(buy.dataset.buy as ItemId);
        const eq = t.closest<HTMLElement>('[data-equip]');
        if (eq) this.onEquip(eq.dataset.equip as ItemId);
      });
    }
    this.refresh();
  }

  get panelOpen(): boolean {
    return [this.shop, this.bag, this.log].some((p) => p.classList.contains('open'));
  }

  refresh(): void {
    this.coinsEl.textContent = String(this.wallet.coins);
    if (this.wallet.coins !== this.lastCoins) {
      const chip = $('#coins');
      chip.classList.remove('bump');
      void chip.offsetWidth;
      chip.classList.add('bump');
      this.lastCoins = this.wallet.coins;
    }
    // Tracker: main quest first, then up to two side quests.
    const active = this.quests.active().slice(0, 3);
    this.tracker.innerHTML = active.length
      ? active.map((q) => `<li class="${q.kind}"><b>${esc(q.title)}</b><span>${esc(this.quests.currentStep(q)!.text)}</span></li>`).join('')
      : '<li class="idle"><span>Find someone with a quest (look for a <em>!</em>)</span></li>';
    if (this.openShopId) this.renderShop(this.openShopId);
    if (this.bag.classList.contains('open')) this.renderBag();
    if (this.log.classList.contains('open')) this.renderLog();
  }

  toast(title: string, sub = '', kind: 'quest' | 'coin' | 'item' | 'info' = 'info'): void {
    const el = document.createElement('div');
    el.className = `toast ${kind}`;
    el.innerHTML = `<b>${esc(title)}</b>${sub ? `<span>${esc(sub)}</span>` : ''}`;
    this.toasts.appendChild(el);
    setTimeout(() => el.classList.add('out'), 2800);
    setTimeout(() => el.remove(), 3300);
  }

  // --- floating markers and speech bubbles, re-used every frame ---
  beginMarkers(): void {
    this.markerUsed = 0;
  }

  marker(x: number, y: number, text: string, cls: string): void {
    let el = this.markerPool[this.markerUsed];
    if (!el) {
      el = document.createElement('div');
      this.markers.appendChild(el);
      this.markerPool.push(el);
    }
    this.markerUsed++;
    if (el.textContent !== text) el.textContent = text;
    if (el.className !== `mk ${cls}`) el.className = `mk ${cls}`;
    el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -100%)`;
    el.style.display = '';
  }

  endMarkers(): void {
    for (let i = this.markerUsed; i < this.markerPool.length; i++) this.markerPool[i].style.display = 'none';
  }

  // --- panels ---
  openShop(shopId: string, vendor: string, color: string, greeting: string): void {
    this.closePanels(false);
    this.openShopId = shopId;
    this.shop.style.setProperty('--speaker', color);
    this.shop.dataset.vendor = vendor;
    this.shop.dataset.greeting = greeting;
    this.renderShop(shopId);
    this.shop.classList.add('open');
  }

  private renderShop(shopId: string): void {
    const shop = SHOPS[shopId];
    const cards = shop.stock.map((id) => {
      const it = ITEMS[id];
      const owned = this.wallet.owns(id);
      const poor = this.wallet.coins < it.price;
      const label = owned ? 'Owned' : poor ? 'Not enough' : 'Buy';
      return `<article class="card${owned ? ' owned' : ''}">
        <img alt="" src="${this.thumbs.get(id)}">
        <h3>${esc(it.name)}</h3><p>${esc(it.blurb)}</p>
        <button data-buy="${id}" ${owned || poor ? 'disabled' : ''}><span class="coin"></span>${it.price}<em>${label}</em></button>
      </article>`;
    });
    this.shop.querySelector('.panel')!.innerHTML = `
      <header><div class="tab">${esc(this.shop.dataset.vendor ?? '')}</div><h2>${esc(shop.name)}</h2>
      <div class="purse"><span class="coin"></span>${this.wallet.coins}</div><button class="x" data-close aria-label="Close">&times;</button></header>
      <p class="greet">${esc(this.shop.dataset.greeting ?? '')}</p>
      <div class="grid">${cards.join('')}</div>`;
  }

  toggleBag(): void {
    const open = !this.bag.classList.contains('open');
    this.closePanels(false);
    if (open) {
      this.renderBag();
      this.bag.classList.add('open');
    }
  }

  private renderBag(): void {
    const items = this.wallet.items();
    const eq = this.wallet.equipped;
    const cards = items.map(({ item, count }) => {
      const wearable = item.slot !== 'none';
      const on = eq.weapon === item.id || eq.shield === item.id;
      return `<article class="card${on ? ' on' : ''}">
        <img alt="" src="${this.thumbs.get(item.id)}">
        <h3>${esc(item.name)}${count > 1 ? ` <small>&times;${count}</small>` : ''}</h3><p>${esc(item.blurb)}</p>
        ${wearable ? `<button data-equip="${item.id}">${on ? 'Unequip' : `Equip ${item.slot}`}</button>` : ''}
      </article>`;
    });
    this.bag.querySelector('.panel')!.innerHTML = `
      <header><h2>Your bag</h2><div class="purse"><span class="coin"></span>${this.wallet.coins}</div><button class="x" data-close aria-label="Close">&times;</button></header>
      ${cards.length ? `<div class="grid">${cards.join('')}</div>` : '<p class="greet">Empty. The Forge is to the east, follow the clanging.</p>'}`;
  }

  toggleLog(): void {
    const open = !this.log.classList.contains('open');
    this.closePanels(false);
    if (open) {
      this.renderLog();
      this.log.classList.add('open');
    }
  }

  private renderLog(): void {
    const row = (id: string) => {
      const q = QUESTS.find((x) => x.id === id)!;
      const s = this.quests.get(id);
      if (s.status === 'available') return '';
      const steps = q.steps.map((st, i) => `<li class="${s.status === 'done' || i < s.step ? 'done' : i === s.step ? 'now' : 'later'}">${esc(st.text)}</li>`).join('');
      return `<section class="q ${q.kind} ${s.status}"><h3>${esc(q.title)} <small>${s.status === 'done' ? 'Complete' : `+${q.reward.coins} coins`}</small></h3><ol>${steps}</ol></section>`;
    };
    const main = row('main');
    const side = QUESTS.filter((q) => q.kind === 'side').map((q) => row(q.id)).join('');
    const found = QUESTS.filter((q) => this.quests.get(q.id).status !== 'available').length;
    this.log.querySelector('.panel')!.innerHTML = `
      <header><h2>Quest log</h2><div class="purse">${found}/${QUESTS.length} found</div><button class="x" data-close aria-label="Close">&times;</button></header>
      <h4>Main quest</h4>${main || '<p class="greet">Find the Herald on the stage by the well.</p>'}
      <h4>Side quests</h4>${side || '<p class="greet">People with a <em>!</em> over their heads need a hand.</p>'}`;
  }

  closePanels(notify = true): void {
    const wasOpen = this.panelOpen;
    for (const p of [this.shop, this.bag, this.log]) p.classList.remove('open');
    this.openShopId = null;
    if (notify && wasOpen) this.onClosePanel();
  }
}
